import React, { useState, forwardRef, useImperativeHandle } from "react";

const injectPlaceholderStyle = () => {
  if (document.getElementById("__num-input-ph")) return;
  const s = document.createElement("style");
  s.id = "__num-input-ph";
  s.textContent = `.num-input::placeholder {
    color: rgba(200,190,255,0.28);
    font-weight: 400;
    font-size: 13px;
  }`;
  document.head.appendChild(s);
};

/* =============================================================
   NumberInputField  — with red-border validation support
============================================================= */
const NumberInputField = ({
  value, onChange, label,
  min = 0, max = 999, step = 1,
  disabled = false, colors,
  required = false, submitted = false,
}) => {
  const [hoverMinus, setHoverMinus] = useState(false);
  const [hoverPlus,  setHoverPlus]  = useState(false);

  const numVal    = value === "" || value === null || value === undefined ? 0 : Number(value);
  const clamp     = (v) => Math.min(max, Math.max(min, v));
  const decrement = () => { if (!disabled) onChange(clamp(numVal - step)); };
  const increment = () => { if (!disabled) onChange(clamp(numVal + step)); };

  // ── FIXED: strictly parse integers only, block decimals ──────────────────
  const handleInput = (e) => {
    if (disabled) return;
    const raw = e.target.value;
    // Empty → allow empty state
    if (raw === "") { onChange(""); return; }
    // Strip any non-digit characters (handles paste of "1.5" → "15")
    const stripped = raw.replace(/[^0-9]/g, "");
    if (stripped === "") { onChange(""); return; }
    const parsed = parseInt(stripped, 10);
    if (!isNaN(parsed)) onChange(clamp(parsed));
  };

  // ── FIXED: block ".", "e", "E", "+", "-" keys at the keyboard level ──────
  const handleKeyDown = (e) => {
    if (
      e.key === "."  ||   // decimal point
      e.key === ","  ||   // some locales use comma as decimal
      e.key === "e"  ||   // scientific notation (e.g. 1e5)
      e.key === "E"  ||
      e.key === "+"  ||
      e.key === "-"       // negative numbers not needed (min=0)
    ) {
      e.preventDefault();
    }
  };

  const canDec = !disabled && numVal > min;
  const canInc = !disabled && numVal < max;

  const isInvalid = required && submitted && (
    value === "" || value === null || value === undefined ||
    value === 0  || value === "0"
  );

  const btnStyle = (hovered, canAct) => ({
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "38px", height: "42px",
    background: hovered && canAct ? colors.rowBg : "transparent",
    border: "none",
    color: hovered && canAct ? colors.text : colors.subtext,
    fontSize: "18px", fontWeight: "300",
    cursor: canAct ? "pointer" : "not-allowed",
    outline: "none", padding: 0,
    transition: "background 0.15s, color 0.15s", flexShrink: 0,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <div style={{
        display: "inline-flex", alignItems: "center",
        backgroundColor: colors.inputBg,
        border: `1px solid ${isInvalid ? "#e74c3c" : colors.inputBorder}`,
        borderRadius: "8px", overflow: "hidden",
        height: "42px", userSelect: "none",
        opacity: disabled ? 0.45 : 1, flexShrink: 0,
        boxShadow: isInvalid ? "0 0 0 2px rgba(231,76,60,0.2)" : "none",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}>
        <button type="button" style={btnStyle(hoverMinus, canDec)}
          onClick={decrement} disabled={!canDec}
          onMouseEnter={() => setHoverMinus(true)}
          onMouseLeave={() => setHoverMinus(false)}>−</button>

        <div style={{ width: "1px", height: "22px", backgroundColor: colors.inputBorder, flexShrink: 0 }} />

        <input
          type="number"
          value={value === 0 || value === "" || value === null || value === undefined ? "" : value}
          placeholder="0"
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={handleInput}
          onKeyDown={handleKeyDown}         // ← blocks . e E + -
          inputMode="numeric"               // ← shows numeric keyboard on mobile (no decimal)
          pattern="[0-9]*"                  // ← HTML5 validation hint: integers only
          className="num-input"
          ref={() => injectPlaceholderStyle()}
          style={{
            width: "46px", height: "42px",
            background: "transparent",
            border: "none", outline: "none",
            color: colors.text,
            caretColor: colors.accent,
            fontSize: "14px", fontWeight: "600",
            textAlign: "center", padding: 0,
            fontFamily: "inherit",
            MozAppearance: "textfield",
            WebkitAppearance: "none",
            cursor: disabled ? "not-allowed" : "text",
          }}
        />

        <div style={{ width: "1px", height: "22px", backgroundColor: colors.inputBorder, flexShrink: 0 }} />

        <button type="button" style={btnStyle(hoverPlus, canInc)}
          onClick={increment} disabled={!canInc}
          onMouseEnter={() => setHoverPlus(true)}
          onMouseLeave={() => setHoverPlus(false)}>+</button>

        {label && (
          <span style={{
            paddingLeft: "6px", paddingRight: "12px",
            color: colors.subtext, fontSize: "13px",
            fontWeight: "500", whiteSpace: "nowrap",
          }}>{label}</span>
        )}
      </div>
      {isInvalid && (
        <span style={{ color: "#e74c3c", fontSize: "11px", fontWeight: "500", paddingLeft: "2px" }}>
          Required
        </span>
      )}
    </div>
  );
};

/* ── Checkbox ── */
const CustomCheckbox = ({ checked, onChange, colors }) => (
  <div onClick={onChange} style={{
    width: "18px", height: "18px", borderRadius: "4px",
    border: `2px solid ${checked ? colors.accent : colors.inputBorder}`,
    backgroundColor: checked ? colors.accent : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", flexShrink: 0, transition: "all 0.2s",
  }}>
    {checked && (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )}
  </div>
);

const CYCLE_LABELS = {
  monthly:       "per month",
  weekly:        "per week",
  quarterly:     "per quarter",
  "half-yearly": "per half year",
  yearly:        "per year",
};

/* ── Policy Section ── */
const PolicySection = ({ prefix, title, icon, rules, set, cycleLabel, colors, submitted }) => {
  const penaltyKey   = `${prefix}Penalty`;
  const amountKey    = `${prefix}Amount`;
  const currencyKey  = `${prefix}Currency`;
  const graceKey     = `${prefix}GraceTime`;
  const instancesKey = `${prefix}Instances`;

  const isSalarySelected = rules[penaltyKey] === "salary";

  const rowStyle = (isChecked, isInvalid = false) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px", borderRadius: "8px",
    backgroundColor: isChecked ? colors.accentLight : colors.rowBg,
    border: `1px solid ${isInvalid ? "#e74c3c" : isChecked ? colors.accent : colors.border}`,
    marginBottom: "8px", cursor: "pointer", transition: "all 0.2s ease",
    boxShadow: isInvalid ? "0 0 0 2px rgba(231,76,60,0.15)" : "none",
  });

  const selectStyle = {
    backgroundColor: colors.inputBg,
    color:           colors.text,
    border:          `1px solid ${colors.inputBorder}`,
    borderRadius:    "8px",
    padding:         "10px 14px",
    fontSize:        "13px",
    outline:         "none",
    height:          "42px",
    boxSizing:       "border-box",
    fontFamily:      "inherit",
  };

  const salaryLabel = prefix === "late"
    ? "Salary deduction per late"
    : "Salary deduction per early exit";

  const penaltyInvalid = submitted && !rules[penaltyKey];
  const amountInvalid  = submitted && isSalarySelected && (!rules[amountKey] || rules[amountKey] === 0 || rules[amountKey] === "");

  return (
    <div style={{ flex: 1, minWidth: "280px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
        <div style={{
          width: "28px", height: "28px", borderRadius: "6px",
          backgroundColor: colors.accentLight,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px",
        }}>{icon}</div>
        <h4 style={{ color: colors.text, fontSize: "15px", fontWeight: "700", margin: 0 }}>{title}</h4>
      </div>

      {/* Grace time */}
      <div style={{ marginBottom: "16px" }}>
        <label style={{ color: colors.subtext, fontSize: "12px", display: "block", marginBottom: "8px", fontWeight: "500" }}>
          {prefix === "late" ? "Grace Time Allowed" : "Early Leave Grace Time"}
        </label>
        <NumberInputField
          value={rules[graceKey]} onChange={(v) => set(graceKey, v)}
          label="min" min={0} max={120} colors={colors}
          submitted={submitted}
        />
      </div>

      {/* Instances */}
      <div style={{ marginBottom: "20px" }}>
        <label style={{ color: colors.subtext, fontSize: "12px", display: "block", marginBottom: "8px", fontWeight: "500" }}>
          {prefix === "late"
            ? `Allow late instances ${cycleLabel}`
            : `Allow early exits ${cycleLabel}`}
        </label>
        <NumberInputField
          value={rules[instancesKey]} onChange={(v) => set(instancesKey, v)}
          label="times" min={0} max={99} colors={colors}
          submitted={submitted}
        />
      </div>

      {/* Penalty label */}
      <label style={{ color: colors.subtext, fontSize: "12px", display: "block", marginBottom: "10px", fontWeight: "500" }}>
        After exceeding limit, apply penalty:
      </label>

      {/* Half Day row */}
      <div style={rowStyle(rules[penaltyKey] === "halfday", penaltyInvalid && !rules[penaltyKey])}
        onClick={() => set(penaltyKey, "halfday")}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <CustomCheckbox checked={rules[penaltyKey] === "halfday"}
            onChange={() => set(penaltyKey, "halfday")} colors={colors} />
          <span style={{ color: colors.text, fontSize: "13px", fontWeight: "500" }}>Deduct Half Day</span>
        </div>
        <span style={{ fontSize: "16px" }}>⚠️</span>
      </div>

      {/* Full Day row */}
      <div style={rowStyle(rules[penaltyKey] === "fullday")}
        onClick={() => set(penaltyKey, "fullday")}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <CustomCheckbox checked={rules[penaltyKey] === "fullday"}
            onChange={() => set(penaltyKey, "fullday")} colors={colors} />
          <span style={{ color: colors.text, fontSize: "13px", fontWeight: "500" }}>Deduct 1 Day Salary</span>
        </div>
        <span style={{ fontSize: "16px" }}>⚠️</span>
      </div>

      {/* Salary row */}
      <div style={{
        display: "flex", alignItems: "center", gap: "10px",
        padding: "10px 16px", borderRadius: "8px",
        backgroundColor: isSalarySelected ? colors.accentLight : colors.rowBg,
        border: `1px solid ${amountInvalid ? "#e74c3c" : isSalarySelected ? colors.accent : colors.border}`,
        marginBottom: amountInvalid ? "4px" : "8px",
        transition: "all 0.2s ease",
        flexWrap: "nowrap",
        boxShadow: amountInvalid ? "0 0 0 2px rgba(231,76,60,0.15)" : "none",
      }}>
        <div
          onClick={() => set(penaltyKey, "salary")}
          style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", flex: "1 1 auto", minWidth: 0 }}
        >
          <CustomCheckbox
            checked={isSalarySelected}
            onChange={() => set(penaltyKey, "salary")}
            colors={colors}
          />
          <span style={{ color: colors.text, fontSize: "13px", fontWeight: "500", whiteSpace: "nowrap" }}>
            {salaryLabel}
          </span>
        </div>

        <NumberInputField
          value={rules[amountKey]}
          onChange={(v) => set(amountKey, v)}
          label=""
          min={0} max={99999} step={100}
          disabled={!isSalarySelected}
          colors={colors}
          required={isSalarySelected}
          submitted={submitted}
        />

        <select
          value={rules[currencyKey] || "INR"}
          onChange={(e) => set(currencyKey, e.target.value)}
          disabled={!isSalarySelected}
          onClick={(e) => e.stopPropagation()}
          style={{
            ...selectStyle,
            width: "90px", flexShrink: 0,
            opacity: isSalarySelected ? 1 : 0.45,
            cursor: isSalarySelected ? "pointer" : "not-allowed",
          }}
        >
          <option value="INR">₹ INR</option>
        
        </select>
      </div>
      {amountInvalid && (
        <div style={{ color: "#e74c3c", fontSize: "11px", fontWeight: "500", paddingLeft: "4px", marginBottom: "8px" }}>
          Please enter a deduction amount
        </div>
      )}
    </div>
  );
};

/* =============================================================
   API SERVICE FUNCTION
============================================================= */
const getMyShiftList = async (userId, shiftId) => {
  try {
    const response = await fetch(`/api/shifts/${userId}/${shiftId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch shift data');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching shift data:', error);
    throw error;
  }
};

/* =============================================================
   MAIN COMPONENT
============================================================= */
const LateComingEarlyLeavingRules = forwardRef(({ 
  theme = "dark", 
  shiftType = "fixed",
  shiftId = null,
  userId = null 
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [submitted, setSubmitted]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const isDark = theme === "dark";

  const [rules, setRules] = useState({
    lateGraceTime:    "",
    lateInstances:    "",
    latePenalty:      "",
    lateAmount:       "",
    lateCurrency:     "INR",
    lateConvertLeave: false,
    lateWarningOnly:  false,
    
    earlyGraceTime:   "",
    earlyInstances:   "",
    earlyPenalty:     "",
    earlyAmount:      "",
    earlyCurrency:    "INR",
    earlyConvertLeave: false,
    earlyWarningOnly: false,
    
    commonResetCycle: "monthly",
  });

  const set = (key, val) => setRules((prev) => ({ ...prev, [key]: val }));
  const cycleLabel = CYCLE_LABELS[rules.commonResetCycle] || "per month";

  const validate = () => {
    setSubmitted(true);
    if (!isExpanded) setIsExpanded(true);
    const errors = [];
    if (rules.latePenalty  === "salary" && (!rules.lateAmount  || rules.lateAmount === "" || rules.lateAmount === 0)) 
      errors.push("Late deduction amount");
    if (rules.earlyPenalty === "salary" && (!rules.earlyAmount || rules.earlyAmount === "" || rules.earlyAmount === 0)) 
      errors.push("Early deduction amount");
    return errors;
  };

  const getData = () => {
    const mapPenalty = (penalty, amount) => {
      if (penalty === "halfday")  return "1";
      if (penalty === "fullday")  return "2";
      if (penalty === "salary")   return amount ? `3|${amount}` : "";
      return "";
    };

    return {
      lateComingPolicy: [{
        graceTimeAllowed:      rules.lateGraceTime ? String(rules.lateGraceTime) : "",
        lateInstancePerMonth:  rules.lateInstances ? String(rules.lateInstances) : "",
        exceedingLimit:        mapPenalty(rules.latePenalty, rules.lateAmount),
      }],
      earlyLeavingPolicy: [{
        graceTimeAllowed:      rules.earlyGraceTime ? String(rules.earlyGraceTime) : "",
        lateInstancePerMonth:  rules.earlyInstances ? String(rules.earlyInstances) : "",
        exceedingLimit:        mapPenalty(rules.earlyPenalty, rules.earlyAmount),
      }],
      resetCycle: rules.commonResetCycle.charAt(0).toUpperCase() + rules.commonResetCycle.slice(1),
    };
  };

  const setData = (apiData) => {
    if (!apiData?.details?.[0]) return;

    const d = apiData.details[0];

    // Parse penalty string: "1" → halfday, "2" → fullday, "3|500" → salary+amount
    const parsePenalty = (str) => {
      if (!str || str === "") return { type: "", amount: "" };
      if (str === "1") return { type: "halfday", amount: "" };
      if (str === "2") return { type: "fullday",  amount: "" };
      if (String(str).startsWith("3")) {
        const parts = String(str).split("|");
        return { type: "salary", amount: parts[1] || "" };
      }
      return { type: "", amount: "" };
    };

    const latePenalty  = parsePenalty(d.lcp_exceeding_limit);
    const earlyPenalty = parsePenalty(d.elp_exceeding_limit);

    // Reset cycle: API often returns "" for old records → default to monthly
    const resetCycle = d.lcp_resetCycle ? d.lcp_resetCycle.toLowerCase() : "monthly";

    setRules({
      // Late coming
      lateGraceTime:    d.lcp_gracetime_allow ? String(parseInt(d.lcp_gracetime_allow)) : "",
      lateInstances:    d.lcp_late_instance_per_month ? String(parseInt(d.lcp_late_instance_per_month)) : "",
      latePenalty:      latePenalty.type,
      lateAmount:       latePenalty.amount,
      lateCurrency:     "INR",
      lateConvertLeave: false,
      lateWarningOnly:  false,

      // Early leaving
      earlyGraceTime:    d.elp_gracetime_allow ? String(parseInt(d.elp_gracetime_allow)) : "",
      earlyInstances:    d.elp_late_instance_per_month ? String(parseInt(d.elp_late_instance_per_month)) : "",
      earlyPenalty:      earlyPenalty.type,
      earlyAmount:       earlyPenalty.amount,
      earlyCurrency:     "INR",
      earlyConvertLeave: false,
      earlyWarningOnly:  false,

      // Shared reset cycle
      commonResetCycle: resetCycle,
    });
  };

  /* =============================================================
     FETCH SHIFT DATA FROM API
  ============================================================= */
  const fetchShiftData = async () => {
    if (!userId || !shiftId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getMyShiftList(userId, shiftId);
      
      // Check if data has the expected structure
      if (data?.details?.[0]) {
        setData(data);
      } else if (data?.workHoursPolicy?.[0]?.lateComingPolicy?.[0]) {
        // Handle alternative API response structure
        const latePolicy = data.workHoursPolicy[0].lateComingPolicy[0];
        const earlyPolicy = data.workHoursPolicy[0].earlyLeavingPolicy[0];
        
        const parsePenalty = (str) => {
          if (!str || str === "") return { type: "", amount: "" };
          if (str === "1") return { type: "halfday", amount: "" };
          if (str === "2") return { type: "fullday", amount: "" };
          if (String(str).startsWith("3")) {
            const parts = String(str).split("|");
            return { type: "salary", amount: parts[1] || "" };
          }
          return { type: "", amount: "" };
        };

        const latePenalty = parsePenalty(latePolicy?.exceedingLimit);
        const earlyPenalty = parsePenalty(earlyPolicy?.exceedingLimit);

        setRules({
          lateGraceTime:    latePolicy?.graceTimeAllowed || "",
          lateInstances:    latePolicy?.lateInstancePerMonth || "",
          latePenalty:      latePenalty.type,
          lateAmount:       latePenalty.amount,
          lateCurrency:     "INR",
          lateConvertLeave: false,
          lateWarningOnly:  false,
          
          earlyGraceTime:   earlyPolicy?.graceTimeAllowed || "",
          earlyInstances:   earlyPolicy?.lateInstancePerMonth || "",
          earlyPenalty:     earlyPenalty.type,
          earlyAmount:      earlyPenalty.amount,
          earlyCurrency:    "INR",
          earlyConvertLeave: false,
          earlyWarningOnly: false,
          
          commonResetCycle: data.workHoursPolicy[0]?.resetCycle?.toLowerCase() || "monthly",
        });
      }
    } catch (err) {
      setError(err.message);
      console.error('Error in fetchShiftData:', err);
    } finally {
      setLoading(false);
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({ 
    validate, 
    getData,
    setData,
    fetchShiftData,
    loading,
    error
  }));

  const colors = {
    cardBg:      isDark ? "#13132b" : "#ffffff",
    sectionBg:   isDark ? "#1a1a35" : "#f9f7fc",
    rowBg:       isDark ? "#1e1e3a" : "#f0ecf8",
    border:      isDark ? "rgba(120,100,200,0.2)" : "rgba(74,42,130,0.15)",
    text:        isDark ? "#e7e6ec" : "#1a0f2e",
    subtext:     isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(74,42,130,0.6)",
    accent:      "#6c5ce7",
    accentLight: isDark ? "rgba(108,92,231,0.3)" : "rgba(108,92,231,0.1)",
    inputBg:     isDark ? "#2a1a3500" : "#ffffff",
    inputBorder: isDark ? "rgba(108,92,231,0.3)" : "rgba(108,92,231,0.25)",
  };

  return (
    <div style={{
      backgroundColor: colors.cardBg, borderRadius: "12px",
      overflow: "hidden", border: `1px solid ${colors.border}`,
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      position: "relative",
    }}>
      {/* Loading overlay */}
      {loading && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          borderRadius: "12px",
        }}>
          <div style={{
            backgroundColor: colors.cardBg,
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}>
            Loading...
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div style={{
          padding: "12px 24px",
          backgroundColor: "#fee",
          color: "#c00",
          borderBottom: "1px solid #fcc",
        }}>
          Error loading data: {error}
        </div>
      )}

      {/* Header */}
      <div onClick={() => setIsExpanded(!isExpanded)} style={{
        padding: "18px 24px", backgroundColor: colors.sectionBg,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: isExpanded ? `1px solid ${colors.border}` : "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: colors.text }}>
              Late Coming &amp; Early Leaving Rules
            </div>
            <div style={{ fontSize: "12px", color: colors.subtext, marginTop: "2px" }}>
              Configure penalty and threshold settings
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: "28px", height: "28px", borderRadius: "6px", border: `1px solid ${colors.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <rect x="1" y="1" width="4" height="4" rx="1" fill={colors.subtext}/>
              <rect x="7" y="1" width="4" height="4" rx="1" fill={colors.subtext}/>
              <rect x="1" y="7" width="4" height="4" rx="1" fill={colors.subtext}/>
              <rect x="7" y="7" width="4" height="4" rx="1" fill={colors.subtext}/>
            </svg>
          </div>
          <div style={{
            width: "28px", height: "28px", borderRadius: "6px", border: `1px solid ${colors.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: colors.subtext, fontSize: "14px",
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s",
          }}>∨</div>
        </div>
      </div>

      {isExpanded && (
        <div style={{ padding: "24px" }}>

          {/* Reset Cycle */}
          <div style={{ padding: "20px 24px", backgroundColor: colors.sectionBg, borderRadius: "12px", border: `1px solid ${colors.accent}`, marginBottom: "28px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              <div>
                <h4 style={{ color: colors.text, fontSize: "15px", fontWeight: "700", margin: 0 }}>Common Reset Cycle</h4>
                <p style={{ color: colors.subtext, fontSize: "12px", margin: "3px 0 0 0" }}>Changing this updates the instance limit labels below</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
              {[
                { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" },
                { value: "quarterly", label: "Quarterly" }, { value: "half-yearly", label: "Half Yearly" },
                { value: "yearly", label: "Yearly" },
              ].map((opt) => {
                const active = rules.commonResetCycle === opt.value;
                return (
                  <div key={opt.value} onClick={() => set("commonResetCycle", opt.value)} style={{
                    padding: "8px 18px", borderRadius: "20px",
                    border: `1px solid ${active ? colors.accent : colors.border}`,
                    backgroundColor: active ? colors.accent : "transparent",
                    color: active ? "#fff" : colors.subtext,
                    fontSize: "13px", fontWeight: active ? "600" : "400",
                    cursor: "pointer", transition: "all 0.2s", userSelect: "none",
                  }}>{opt.label}</div>
                );
              })}
            </div>
            <div style={{ marginTop: "12px", padding: "8px 14px", backgroundColor: colors.accentLight, borderRadius: "8px", border: `1px solid ${colors.inputBorder}`, display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "13px" }}>ⓘ</span>
              <span style={{ color: colors.subtext, fontSize: "12px" }}>
                Instance limits will count <strong style={{ color: colors.text }}>{CYCLE_LABELS[rules.commonResetCycle]}</strong> and reset automatically
              </span>
            </div>
          </div>

          {/* Two policy sections */}
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
            <PolicySection prefix="late"  title="Late Coming Policy"  icon="🕐" rules={rules} set={set} cycleLabel={cycleLabel} colors={colors} submitted={submitted} />
            <PolicySection prefix="early" title="Early Leaving Policy" icon="🚪" rules={rules} set={set} cycleLabel={cycleLabel} colors={colors} submitted={submitted} />
          </div>

        </div>
      )}
    </div>
  );
});

export default LateComingEarlyLeavingRules;